import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import api from '../../api';
import useServerZones from '../../hooks/useServerZones';
import { useAccess } from '../../contexts/AccessContext';
import './ReservationPage.css';

let nextRowId = 1;

const ReservationPage = () => {
    const { serverId, sessionId: urlSessionId } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { success, error: notifError } = useNotification();
    const { can } = useAccess();

    // Drag & drop
    const [draggingId, setDraggingId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);
    const dragRowId = useRef(null);
    const sessionsRef = useRef([]); // ref pour éviter stale closures dans les callbacks

    // Saisons du serveur
    const [saisons, setSaisons] = useState([]);
    const [loadingSaisons, setLoadingSaisons] = useState(true);
    // Fusions enregistrées (page Fusion), proposées comme des saisons
    const [fusions, setFusions] = useState([]);
    const fusionIdsRef = useRef(new Set());

    // Sessions (fiches de réservation)
    const [sessions, setSessions] = useState([]);
    const [activeSessionId, setActiveSessionId] = useState(urlSessionId || '');
    const [savingRows, setSavingRows] = useState(false);
    const deletedReservationIds = useRef([]);

    // Fiche en cours (visible membres)
    const [liveSessionId, setLiveSessionId] = useState('');
    const [settingLive, setSettingLive] = useState(false);

    // Publication
    const [channels, setChannels] = useState([]);
    const [publishChannelId, setPublishChannelId] = useState('');
    const [publishing, setPublishing] = useState(false);

    // Modale préférences admin
    const [editingPrefsModal, setEditingPrefsModal] = useState(null); // { joueurId, prefs }

    // Lignes du tableau : [{ id, _reservationId, saisonId, joueurId, zone, donjon, _zoneOverride }]
    const [rows, setRows] = useState([{ id: nextRowId++, _reservationId: null, saisonId: '', joueurId: '', zone: '', donjon: '', _zoneOverride: false, _zoneReason: null }]);

    // Cache des classements par saisonId : { [saisonId]: [{joueurId, totalPoints, rank}, ...] }
    const classementsCache = useRef({});
    const loadingClassements = useRef({});

    // Cache des noms de joueurs : { [joueurId]: string }
    const [usernamesCache, setUsernamesCache] = useState({});

    // Zones réservables du serveur (page Zones, hors zones privatisées BDA)
    const { availableZones: serverZones } = useServerZones(serverId);

    // Cache des préférences de zones : { [joueurId]: [{zone, donjon}] }
    const [zonePrefsCache, setZonePrefsCache] = useState({});

    // Forcer le re-render quand le cache classement est mis à jour
    const [cacheVersion, setCacheVersion] = useState(0);

    // ─── Chargement config + salons + saisons ────────────────────────────────
    useEffect(() => {
        if (!serverId) return;

        const loadAll = async () => {
            setLoadingSaisons(true);
            try {
                const [saisonsRes, fusionsRes, sessionsRes, channelsRes, configRes] = await Promise.all([
                    api.get(`/servers/${serverId}/saisons`).catch(() => ({ data: [] })),
                    api.get(`/servers/${serverId}/saisons-fusion`).catch(() => ({ data: [] })),
                    api.get(`/reservation-sessions/${serverId}`).catch(() => ({ data: [] })),
                    api.get(`/discord/channels/${serverId}`).catch(() => ({ data: [] })),
                    api.get(`/reservation-config/${serverId}`).catch(() => ({ data: {} }))
                ]);
                const sessionsData = Array.isArray(sessionsRes.data) ? sessionsRes.data : [];
                setSaisons(Array.isArray(saisonsRes.data) ? saisonsRes.data : []);
                const fusionsData = Array.isArray(fusionsRes.data) ? fusionsRes.data : [];
                fusionIdsRef.current = new Set(fusionsData.map(f => f._id));
                setFusions(fusionsData);
                setSessions(sessionsData);
                // Sync immédiat du ref avant de charger les réservations (évite la race condition frozen)
                sessionsRef.current = sessionsData;
                const textChannels = (Array.isArray(channelsRes.data) ? channelsRes.data : [])
                    .filter(c => c.type === 0)
                    .sort((a, b) => a.position - b.position);
                setChannels(textChannels);
                setLiveSessionId(configRes.data?.activeSessionId || '');
                if (urlSessionId) {
                    setActiveSessionId(urlSessionId);
                    // Recharger les lignes maintenant que sessionsRef est à jour (frozen correct)
                    await loadSessionReservations(urlSessionId);
                }
            } catch {
                notifError(t('resaEdit.configLoadError', '❌ Impossible de charger la configuration'));
            } finally {
                setLoadingSaisons(false);
            }
        };

        loadAll();
    }, [serverId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Synchroniser sessionsRef pour les callbacks (évite stale closure)
    useEffect(() => { sessionsRef.current = sessions; }, [sessions]);

    // ─── Chargement des réservations d'une session ────────────────────────────
    const loadSessionReservations = useCallback(async (sessionId) => {
        if (!sessionId) {
            setRows([{ id: nextRowId++, _reservationId: null, saisonId: '', joueurId: '', zone: '', donjon: '', _zoneOverride: false, _zoneReason: null }]);
            deletedReservationIds.current = [];
            return;
        }
        try {
            const isFrozen = sessionsRef.current.find(s => s._id === sessionId)?.frozen || false;
            const res = await api.get(`/reservations/${serverId}`, { params: { sessionId } });
            const data = Array.isArray(res.data) ? res.data : [];
            if (data.length === 0) {
                setRows([{ id: nextRowId++, _reservationId: null, saisonId: '', joueurId: '', zone: '', donjon: '', _zoneOverride: false, _zoneReason: null }]);
            } else {
                setRows(data.map(r => ({
                    id: nextRowId++,
                    _reservationId: r._id,
                    saisonId: r.saisonId || '',
                    joueurId: r.joueurId || '',
                    zone: r.zone || '',
                    donjon: r.donjon || '',
                    _zoneOverride: !!r._zoneOverride,
                    _zoneReason: isFrozen ? 'frozen' : (r._zoneReason || null)
                })));
            }
            deletedReservationIds.current = [];
        } catch {
            notifError(t('resaEdit.loadError', '❌ Impossible de charger les réservations'));
        }
    }, [serverId, notifError]); // eslint-disable-line react-hooks/exhaustive-deps

    // Recharger quand la session active change
    useEffect(() => {
        if (activeSessionId) {
            loadSessionReservations(activeSessionId);
        } else {
            setRows([{ id: nextRowId++, _reservationId: null, saisonId: '', joueurId: '', zone: '', donjon: '', _zoneOverride: false, _zoneReason: null }]);
            deletedReservationIds.current = [];
        }
    }, [activeSessionId, loadSessionReservations]);

    // ─── Sauvegarde des lignes ────────────────────────────────────────────────
    const saveSessionRows = async () => {
        if (!activeSessionId) { notifError(t('resaEdit.pickSheetFirst', '❌ Sélectionne ou crée une fiche d\'abord')); return; }
        setSavingRows(true);
        try {
            // Remplace toutes les réservations de la session en un seul appel atomique
            await api.put(`/reservations/bulk/${serverId}/${activeSessionId}`, {
                rows: rows.map((row, index) => ({
                    saisonId: row.saisonId || '',
                    joueurId: row.joueurId,
                    zone: row.zone || '',
                    donjon: row.donjon || '',
                    rank: index + 1
                }))
            });

            deletedReservationIds.current = [];

            // Resynchroniser pour récupérer les _id MongoDB
            await loadSessionReservations(activeSessionId);
            success(t('resaEdit.saved', '✅ Fiche sauvegardée !'));
        } catch {
            notifError(t('resaEdit.saveError', '❌ Erreur lors de la sauvegarde'));
        } finally {
            setSavingRows(false);
        }
    };

    // ─── Définir comme fiche en cours ────────────────────────────────────────────
    const setAsLiveSession = async () => {
        if (!activeSessionId) return;
        setSettingLive(true);
        try {
            const newLive = liveSessionId === activeSessionId ? '' : activeSessionId;
            await api.put(`/reservation-config/${serverId}`, { activeSessionId: newLive });
            setLiveSessionId(newLive);
            success(newLive ? t('resaEdit.liveSet', '📌 Fiche définie comme fiche en cours !') : t('resaEdit.liveUnset', '📌 Fiche en cours retirée'));
        } catch {
            notifError(t('resaEdit.updateError', '❌ Erreur lors de la mise à jour'));
        } finally {
            setSettingLive(false);
        }
    };

    // ─── Supprimer la fiche active ───────────────────────────────────────────────
    const deleteSession = async () => {
        if (!activeSessionId) return;
        const sessionName = sessions.find(s => s._id === activeSessionId)?.title || t('resaEdit.thisSheet', 'cette fiche');
        if (!window.confirm(t('resaList.confirmDelete', 'Supprimer « {title} » ? Cette action est irréversible.', { title: sessionName }))) return;
        try {
            await api.delete(`/reservation-sessions/${activeSessionId}`);
            if (liveSessionId === activeSessionId) setLiveSessionId('');
            success(t('resaList.deleted', '🗑️ Fiche supprimée'));
            navigate(`/server/${serverId}/reservation`);
        } catch {
            notifError(t('resaList.deleteError', '❌ Erreur lors de la suppression'));
        }
    };

    // ─── Publication dans un salon Discord ────────────────────────────────────────
    const publishSession = async () => {
        if (!activeSessionId || !publishChannelId) return;
        setPublishing(true);
        try {
            await api.post(`/reservations/publish/${serverId}/${activeSessionId}`, { channelId: publishChannelId });
            success(t('resaEdit.published', '✅ Fiche publiée dans le salon Discord !'));
        } catch {
            notifError(t('resaEdit.publishError', '❌ Erreur lors de la publication'));
        } finally {
            setPublishing(false);
        }
    };

    // ─── Geler / Dégeler la fiche ─────────────────────────────────────────────────
    const toggleFreeze = async () => {
        if (!activeSessionId) return;
        const session = sessionsRef.current.find(s => s._id === activeSessionId);
        if (!session) return;
        const newFrozen = !session.frozen;
        try {
            await api.put(`/reservation-sessions/${activeSessionId}`, { frozen: newFrozen });
            setSessions(prev => prev.map(s => s._id === activeSessionId ? { ...s, frozen: newFrozen } : s));
            if (newFrozen) {
                setRows(prev => prev.map(r => r.joueurId ? { ...r, _zoneOverride: false, _zoneReason: 'frozen' } : r));
                success(t('resaEdit.frozenToast', '🔒 Fiche figée — les zones ne seront plus recalculées automatiquement'));
            } else {
                await loadSessionReservations(activeSessionId);
                success(t('resaEdit.unfrozenToast', '🔓 Fiche défigée — l\'auto-attribution est réactivée'));
            }
        } catch {
            notifError(t('resaEdit.statusError', '❌ Erreur lors du changement de statut'));
        }
    };

    // ─── Ouvrir / Fermer la fenêtre d'échange ────────────────────────────────────
    const toggleSwapWindow = async () => {
        if (!activeSessionId) return;
        const session = sessionsRef.current.find(s => s._id === activeSessionId);
        if (!session) return;
        const newOpen = !session.swapWindowOpen;
        try {
            await api.put(`/reservation-sessions/${activeSessionId}`, { swapWindowOpen: newOpen });
            setSessions(prev => prev.map(s => s._id === activeSessionId ? { ...s, swapWindowOpen: newOpen } : s));
            success(newOpen ? t('resaEdit.swapOpened', '🔄 Fenêtre d\'échange ouverte — les membres peuvent changer de zone !') : t('resaEdit.swapClosed', '🔒 Fenêtre d\'échange fermée'));
        } catch {
            notifError(t('resaEdit.changeError', '❌ Erreur lors du changement'));
        }
    };

    // ─── Sauvegarde des préférences d'un joueur ───────────────────────────────────
    const savePrefsForPlayer = async () => {
        if (!editingPrefsModal) return;
        const { joueurId, prefs } = editingPrefsModal;
        const validPrefs = prefs.filter(p => p.zone);
        try {
            await api.put(`/zone-preferences/${serverId}/for/${joueurId}`, { preferences: validPrefs });
            setZonePrefsCache(prev => ({ ...prev, [joueurId]: validPrefs }));
            setEditingPrefsModal(null);
            success(t('resaEdit.prefsSaved', '✅ Préférences sauvegardées pour {name}', { name: getPlayerName(joueurId) }));
        } catch {
            notifError(t('resaEdit.prefsSaveError', '❌ Erreur lors de la sauvegarde des préférences'));
        }
    };

    // ─── Résolution des noms via bulk API ─────────────────────────────────────
    const resolveUsernames = useCallback(async (joueurIds) => {
        const toResolve = joueurIds.filter(id => id && !usernamesCache[id]);
        if (toResolve.length === 0) return;

        try {
            // Étape 1 : modèle Joueur (MongoDB)
            const res1 = await api.post('/players/display-names', {
                playerIds: toResolve,
                serverId
            });
            if (res1.data?.success) {
                setUsernamesCache(prev => ({ ...prev, ...res1.data.displayNames }));
                const notFound = res1.data.notFound || [];
                if (notFound.length > 0) {
                    // Étape 2 : Discord API
                    const res2 = await api.post('/discord/users', {
                        userIds: notFound,
                        serverId,
                        forceRefresh: false
                    });
                    setUsernamesCache(prev => ({ ...prev, ...res2.data }));
                }
            }
        } catch {
            // Silencieux : on garde les joueurId comme fallback
        }
    }, [serverId, usernamesCache]);

    // ─── Chargement du classement pour une saison ─────────────────────────────
    const loadClassement = useCallback(async (saisonId) => {
        if (!saisonId) return;
        if (classementsCache.current[saisonId] || loadingClassements.current[saisonId]) return;

        loadingClassements.current[saisonId] = true;
        try {
            const res = fusionIdsRef.current.has(saisonId)
                ? await api.get(`/saisons-fusion/${saisonId}/classement`)
                : await api.get(`/saison/${saisonId}/classement`);
            const data = Array.isArray(res.data) ? res.data : [];
            // Ajouter le rang (déjà trié desc par totalPoints côté API)
            classementsCache.current[saisonId] = data.map((j, i) => ({ ...j, rank: i + 1 }));
            setCacheVersion(v => v + 1);
            // Résoudre les noms de cette saison
            await resolveUsernames(data.map(j => j.joueurId));
        } catch {
            classementsCache.current[saisonId] = [];
            setCacheVersion(v => v + 1);
        } finally {
            loadingClassements.current[saisonId] = false;
        }
    }, [resolveUsernames]);

    // Pré-charger le classement quand une ligne change de saison
    // (après le chargement des fusions, sinon une fusion serait interrogée comme une saison)
    useEffect(() => {
        if (loadingSaisons) return;
        const saisonIds = [...new Set(rows.map(r => r.saisonId).filter(Boolean))];
        saisonIds.forEach(id => loadClassement(id));
    }, [rows, loadClassement, loadingSaisons]);

    // ─── Chargement des préférences de zones ─────────────────────────────────
    useEffect(() => {
        const joueurIds = rows.map(r => r.joueurId).filter(Boolean);
        const toFetch = joueurIds.filter(id => !(id in zonePrefsCache));
        if (toFetch.length === 0 || !serverId) return;

        api.post(`/zone-preferences/${serverId}/bulk`, { userIds: toFetch })
            .then(res => setZonePrefsCache(prev => ({ ...prev, ...res.data })))
            .catch(() => {});
    }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Gestion des lignes ───────────────────────────────────────────────────
    const addRow = () => {
        setRows(prev => [...prev, { id: nextRowId++, _reservationId: null, saisonId: '', joueurId: '', zone: '', donjon: '', _zoneOverride: false, _zoneReason: null }]);
    };

    // Ajout en masse : N lignes déjà réglées sur une saison
    const [bulkCount, setBulkCount] = useState('15');
    const [bulkSaisonId, setBulkSaisonId] = useState('');
    const addBulkRows = () => {
        const count = Math.min(Math.max(parseInt(bulkCount, 10) || 0, 0), 200);
        if (count === 0) return;
        setRows(prev => [
            // Les lignes vides jamais enregistrées (ex. la ligne de départ) sont remplacées
            ...prev.filter(r => r._reservationId || r.saisonId || r.joueurId),
            ...Array.from({ length: count }, () => ({ id: nextRowId++, _reservationId: null, saisonId: bulkSaisonId, joueurId: '', zone: '', donjon: '', _zoneOverride: false, _zoneReason: null })),
        ]);
    };

    const removeRow = (rowId) => {
        setRows(prev => {
            const row = prev.find(r => r.id === rowId);
            if (row?._reservationId) {
                deletedReservationIds.current = [...deletedReservationIds.current, row._reservationId];
            }
            return prev.filter(r => r.id !== rowId);
        });
    };

    const updateRow = (rowId, field, value) => {
        setRows(prev => prev.map(r => {
            if (r.id !== rowId) return r;
            // Réinitialiser joueur et zone si on change de saison
            if (field === 'saisonId') return { ...r, saisonId: value, joueurId: '', zone: '', donjon: '' };
            // Réinitialiser la zone si on change de joueur
            if (field === 'joueurId') return { ...r, joueurId: value, zone: '', donjon: '', _zoneOverride: false, _zoneReason: null };
            return { ...r, [field]: value };
        }));
    };

    // ─── Auto-attribution des zones (déclenché automatiquement) ──────────────
    const joueurIdsKey = rows.map(r => `${r.id}:${r.saisonId}:${r.joueurId}:${r._zoneOverride ? '1' : '0'}:${r._zoneReason === 'frozen' ? 'f' : '0'}`).join('|');

    useEffect(() => {
        // La priorité d'attribution suit l'ordre des lignes dans le tableau.
        // Drag & drop = l'admin choisit explicitement l'ordre de priorité.

        // Réserver d'abord les zones figées et les overrides manuels
        const takenZones = new Set(
            rows.filter(r => (r._zoneOverride || r._zoneReason === 'frozen') && r.zone).map(r => r.zone)
        );

        const assignments = {};
        // Préférences limitées aux zones encore proposées par le serveur
        const zonesByName = new Map(serverZones.map(z => [z.zone, z]));
        for (const row of rows) {
            if (row._zoneReason === 'frozen') {
                assignments[row.id] = { zone: row.zone, donjon: row.donjon, reason: 'frozen' };
                continue;
            }
            if (row._zoneOverride) {
                assignments[row.id] = { zone: row.zone, donjon: row.donjon, reason: 'manual' };
                continue;
            }
            if (!row.joueurId) {
                assignments[row.id] = { zone: '', donjon: '', reason: null };
                continue;
            }
            const prefs = (zonePrefsCache[row.joueurId] || []).filter(p => zonesByName.has(p.zone)).map(p => zonesByName.get(p.zone));
            const isDefault = prefs.length === 0;
            const effectivePrefs = isDefault ? serverZones : prefs;
            let assigned = { zone: '', donjon: '', reason: isDefault ? 'conflict' : 'conflict' };
            for (const pref of effectivePrefs) {
                if (pref.zone && !takenZones.has(pref.zone)) {
                    assigned = { zone: pref.zone, donjon: pref.donjon || '', reason: isDefault ? 'auto_default' : 'auto' };
                    takenZones.add(pref.zone);
                    break;
                }
            }
            assignments[row.id] = assigned;
        }

        setRows(prev => prev.map(r => {
            if (r._zoneOverride || r._zoneReason === 'frozen') return r;
            return {
                ...r,
                zone: assignments[r.id]?.zone ?? r.zone,
                donjon: assignments[r.id]?.donjon ?? r.donjon,
                _zoneReason: assignments[r.id]?.reason ?? null
            };
        }));
    }, [joueurIdsKey, zonePrefsCache, cacheVersion, serverZones]); // eslint-disable-line

    const setManualOverride = (rowId) => {
        setRows(prev => prev.map(r => r.id === rowId ? { ...r, _zoneOverride: true, _zoneReason: null } : r));
    };

    const resetZoneOverride = (rowId) => {
        setRows(prev => prev.map(r => r.id === rowId ? { ...r, _zoneOverride: false, zone: '', donjon: '', _zoneReason: null } : r));
    };

    // ─── Drag & Drop ──────────────────────────────────────────────────────────
    const handleDragStart = (e, rowId) => {
        dragRowId.current = rowId;
        setDraggingId(rowId);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, rowId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (rowId !== dragRowId.current) setDragOverId(rowId);
    };

    const handleDrop = (e, rowId) => {
        e.preventDefault();
        if (!dragRowId.current || dragRowId.current === rowId) {
            setDraggingId(null);
            setDragOverId(null);
            return;
        }
        setRows(prev => {
            const fromIndex = prev.findIndex(r => r.id === dragRowId.current);
            const toIndex = prev.findIndex(r => r.id === rowId);
            if (fromIndex === -1 || toIndex === -1) return prev;
            const newRows = [...prev];
            const [moved] = newRows.splice(fromIndex, 1);
            newRows.splice(toIndex, 0, moved);
            return newRows;
        });
        dragRowId.current = null;
        setDraggingId(null);
        setDragOverId(null);
    };

    const handleDragEnd = () => {
        dragRowId.current = null;
        setDraggingId(null);
        setDragOverId(null);
    };

    // ─── Helpers d'affichage ─────────────────────────────────────────────────
    const getPlayerName = (joueurId) =>
        usernamesCache[joueurId] || joueurId || '—';

    // Options du choix de saison (saisons + fusions enregistrées)
    const seasonOptions = fusions.length > 0 ? (
        <>
            <optgroup label={t('resaEdit.groupSeasons', 'Saisons')}>
                {saisons.map(s => <option key={s._id} value={s._id}>{s.title}</option>)}
            </optgroup>
            <optgroup label={t('resaEdit.groupFusions', 'Fusions')}>
                {fusions.map(f => <option key={f._id} value={f._id}>{f.title}</option>)}
            </optgroup>
        </>
    ) : (
        saisons.map(s => <option key={s._id} value={s._id}>{s.title}</option>)
    );

    const getClassement = (saisonId) =>
        classementsCache.current[saisonId] || null;

    const isLoadingClassement = (saisonId) =>
        saisonId && !classementsCache.current[saisonId] && loadingClassements.current[saisonId];

    // ─── Render ───────────────────────────────────────────────────────────────
    if (loadingSaisons) {
        return (
            <div className="c2-scope c2-admin-page">
                <div className="c2-loading">
                    <div className="c2-spinner" />
                    <p>{t('common.loading')}</p>
                </div>
            </div>
        );
    }

    const session = sessions.find(s => s._id === activeSessionId);
    const isLive = !!activeSessionId && liveSessionId === activeSessionId;
    const assignedCount = rows.filter(r => r.joueurId && r.zone).length;

    const openPrefs = (row, fallbackToAll = false) => setEditingPrefsModal({
        joueurId: row.joueurId,
        prefs: zonePrefsCache[row.joueurId]?.length > 0 || !fallbackToAll
            ? [...(zonePrefsCache[row.joueurId] || [])]
            : [...serverZones]
    });

    const PrefsButton = ({ row, fallbackToAll }) => (
        <button
            type="button"
            className="c2-icon-btn c2-icon-btn--sm"
            onClick={() => openPrefs(row, fallbackToAll)}
            title={t('resaEdit.editPrefs', 'Modifier les préférences de zone du joueur')}
            aria-label={t('resaEdit.editPrefs', 'Modifier les préférences de zone du joueur')}
        >
            <Icon name="list" size={16} />
        </button>
    );

    const EditZoneButton = ({ row, label }) => (
        <button
            type="button"
            className={label ? 'c2-btn c2-btn--ghost c2-btn--sm' : 'c2-icon-btn c2-icon-btn--sm'}
            onClick={() => setManualOverride(row.id)}
            title={t('resaEdit.pickZoneManually', 'Choisir la zone manuellement')}
            aria-label={t('resaEdit.pickZoneManually', 'Choisir la zone manuellement')}
        >
            <Icon name="pencil" size={16} />
            {label}
        </button>
    );

    const renderZone = (row) => {
        if (!row.joueurId) return <span className="c2-muted">—</span>;

        if (row._zoneOverride) {
            return (
                <div className="resa-zone resa-zone--manual">
                    <select
                        className="c2-select c2-input--sm"
                        value={row.zone}
                        onChange={e => {
                            const zone = e.target.value;
                            setRows(prev => prev.map(r => {
                                if (r.id !== row.id) return r;
                                const zData = serverZones.find(z => z.zone === zone);
                                return { ...r, zone, donjon: zData?.donjon || '' };
                            }));
                        }}
                        aria-label={t('resaEdit.manualZone', 'Zone choisie manuellement')}
                    >
                        <option value="">{t('reservation.selectZone')}</option>
                        {serverZones.map(z => <option key={z.zone} value={z.zone}>{z.zone}</option>)}
                    </select>
                    <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={() => resetZoneOverride(row.id)} title={t('resaEdit.backToAuto', 'Revenir à l\'attribution automatique')}>
                        <Icon name="refresh" size={14} />
                        {t('resaEdit.auto', 'Auto')}
                    </button>
                    {row.donjon && <span className="resa-zone__donjon">{row.donjon}</span>}
                </div>
            );
        }

        const status = {
            frozen: { chip: 'resa-chip--frozen', icon: 'lock', text: row.zone || '—', hint: t('resaEdit.zoneFrozen', 'Zone figée') },
            auto: { chip: 'c2-chip--green', icon: 'map', text: row.zone, hint: null, prefs: true },
            auto_default: { chip: 'resa-chip--default', icon: 'map', text: row.zone, hint: t('resaEdit.defaultOrder', 'Ordre par défaut (pas de préférences)'), prefs: true, fallbackToAll: true },
            conflict: { chip: 'resa-chip--error', icon: 'close', text: t('resaEdit.conflict', 'Conflit'), hint: t('resaEdit.conflictHint', 'Toutes ses préférences sont prises'), prefs: true, editLabel: t('resaEdit.set', 'Définir') },
            no_prefs: { chip: 'resa-chip--error', icon: 'user', text: t('resaEdit.noPrefs', 'Aucune préférence'), hint: t('resaEdit.noPrefsHint', 'Le joueur n\'a pas rempli ses préférences'), prefs: true, editLabel: t('resaEdit.set', 'Définir') }
        }[row._zoneReason];
        if (!status) return <span className="c2-muted">—</span>;

        return (
            <div className="resa-zone">
                <div className="resa-zone__info">
                    <span className={`c2-chip ${status.chip}`}><Icon name={status.icon} size={14} />{status.text}</span>
                    {row.donjon && row._zoneReason !== 'conflict' && row._zoneReason !== 'no_prefs' && <span className="resa-zone__donjon">{row.donjon}</span>}
                    {status.hint && <span className="resa-zone__hint">{status.hint}</span>}
                </div>
                <div className="resa-zone__actions">
                    <EditZoneButton row={row} label={status.editLabel} />
                    {status.prefs && <PrefsButton row={row} fallbackToAll={status.fallbackToAll} />}
                </div>
            </div>
        );
    };

    return (
        <div className="c2-scope c2-admin-page">
            <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm resa-edit__back" onClick={() => navigate(`/server/${serverId}/reservation`)}>
                <Icon name="arrowLeft" size={16} />
                {t('resaEdit.back', 'Fiches de réservation')}
            </button>

            <PageHeader
                icon="clipboard"
                title={session?.title || t('member.nav.sheet', 'Fiche de réservation')}
                subtitle={t('resaEdit.subtitle', '{players} joueur(s) · {zones} zone(s) attribuée(s)', { players: rows.filter(r => r.joueurId).length, zones: assignedCount })}
                actions={
                    <>
                        {can(serverId, 'encheres') && activeSessionId && (
                            <button
                                type="button"
                                className="c2-btn c2-btn--ghost"
                                onClick={() => navigate(`/server/${serverId}/encheres?session=${activeSessionId}`)}
                                title={t('resaEdit.auctionTitle', 'Mettre aux enchères les zones restées libres sur cette fiche')}
                            >
                                <Icon name="hammer" size={18} />
                                {t('auctionAdmin.launch', 'Lancer les enchères')}
                            </button>
                        )}
                        <button type="button" className="c2-btn c2-btn--ghost" onClick={addRow}>
                            <Icon name="plus" size={18} />
                            {t('reservation.addRow')}
                        </button>
                        <button type="button" className="c2-btn c2-btn--primary" onClick={saveSessionRows} disabled={savingRows || !activeSessionId}>
                            <Icon name="save" size={18} />
                            {savingRows ? t('editSeason.saving', 'Enregistrement...') : t('resaEdit.saveSheet', 'Enregistrer la fiche')}
                        </button>
                    </>
                }
            />

            {/* ── État de la fiche ── */}
            <div className="resa-toggles">
                <button
                    type="button"
                    className={`c2-tile resa-toggle${isLive ? ' is-on' : ''}`}
                    onClick={setAsLiveSession}
                    disabled={settingLive || !activeSessionId}
                    aria-pressed={isLive}
                >
                    <span className="resa-toggle__icon"><Icon name="pin" size={20} /></span>
                    <span className="resa-toggle__text">
                        <strong>{isLive ? t('resaEdit.isLive', 'Fiche en cours') : t('resaEdit.setLive', 'Définir en cours')}</strong>
                        <span>{isLive ? t('resaEdit.isLiveHint', 'Visible par les membres') : t('resaEdit.setLiveHint', 'Rendre visible aux membres')}</span>
                    </span>
                </button>
                <button
                    type="button"
                    className={`c2-tile resa-toggle resa-toggle--purple${session?.frozen ? ' is-on' : ''}`}
                    onClick={toggleFreeze}
                    disabled={!activeSessionId}
                    aria-pressed={!!session?.frozen}
                >
                    <span className="resa-toggle__icon"><Icon name="lock" size={20} /></span>
                    <span className="resa-toggle__text">
                        <strong>{session?.frozen ? t('resaEdit.isFrozen', 'Fiche figée') : t('resaEdit.freeze', 'Figer la fiche')}</strong>
                        <span>{session?.frozen ? t('resaEdit.isFrozenHint', 'Les zones ne bougent plus') : t('resaEdit.freezeHint', 'Bloquer les zones attribuées')}</span>
                    </span>
                </button>
                <button
                    type="button"
                    className={`c2-tile resa-toggle${session?.swapWindowOpen ? ' is-on' : ''}`}
                    onClick={toggleSwapWindow}
                    disabled={!activeSessionId}
                    aria-pressed={!!session?.swapWindowOpen}
                >
                    <span className="resa-toggle__icon"><Icon name="swap" size={20} /></span>
                    <span className="resa-toggle__text">
                        <strong>{session?.swapWindowOpen ? t('resaEdit.swapsOpen', 'Échanges ouverts') : t('resaEdit.openSwaps', 'Ouvrir les échanges')}</strong>
                        <span>{session?.swapWindowOpen ? t('resaEdit.swapsOpenHint', 'Les membres peuvent changer de zone') : t('resaEdit.openSwapsHint', 'Laisser les membres changer de zone')}</span>
                    </span>
                </button>
            </div>

            {/* ── Tableau de réservations ── */}
            <section className="c2-card c2-section">
                <div className="c2-section__head">
                    <h2 className="c2-section__title"><Icon name="list" size={20} />{t('reservation.tableTitle')}</h2>
                    <span className="c2-muted resa-edit__hint">{t('resaEdit.dragHint', 'Glisse les lignes pour changer l\'ordre de priorité')}</span>
                </div>

                <div className="c2-table-wrap">
                    <table className="c2-table resa-table">
                        <thead>
                            <tr>
                                <th aria-label={t('prefs.move', 'Déplacer')}></th>
                                <th className="is-center">#</th>
                                <th>{t('reservation.colSaison')}</th>
                                <th>{t('reservation.colJoueur')}</th>
                                <th>{t('reservation.colZone')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => {
                                const classement = getClassement(row.saisonId);
                                const loadingCls = isLoadingClassement(row.saisonId);
                                const rowClass = [
                                    draggingId === row.id && 'is-dragging',
                                    dragOverId === row.id && draggingId !== row.id && 'is-drag-over'
                                ].filter(Boolean).join(' ');

                                return (
                                    <tr
                                        key={row.id}
                                        draggable
                                        onDragStart={e => handleDragStart(e, row.id)}
                                        onDragOver={e => handleDragOver(e, row.id)}
                                        onDrop={e => handleDrop(e, row.id)}
                                        onDragEnd={handleDragEnd}
                                        className={rowClass || undefined}
                                    >
                                        <td className="resa-table__grip" title={t('resaEdit.dragToReorder', 'Glisser pour réordonner')}>
                                            <Icon name="grip" size={16} />
                                        </td>
                                        <td className="is-center c2-muted">{index + 1}</td>
                                        <td>
                                            <select
                                                className="c2-select c2-input--sm"
                                                value={row.saisonId}
                                                onChange={e => updateRow(row.id, 'saisonId', e.target.value)}
                                                aria-label={t('memberStats.season', 'Saison')}
                                            >
                                                <option value="">{t('reservation.selectSaison')}</option>
                                                {seasonOptions}
                                            </select>
                                        </td>
                                        <td>
                                            {!row.saisonId ? (
                                                <span className="c2-muted">{t('reservation.selectSaisonFirst')}</span>
                                            ) : loadingCls ? (
                                                <span className="c2-muted">{t('common.loading')}</span>
                                            ) : classement && classement.length > 0 ? (
                                                <select
                                                    className="c2-select c2-input--sm"
                                                    value={row.joueurId}
                                                    onChange={e => updateRow(row.id, 'joueurId', e.target.value)}
                                                    aria-label={t('sheet.player', 'Joueur')}
                                                >
                                                    <option value="">{t('reservation.selectJoueur')}</option>
                                                    {classement.map(j => (
                                                        <option key={j.joueurId} value={j.joueurId}>
                                                            #{j.rank} — {getPlayerName(j.joueurId)} ({j.totalPoints} pts)
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className="c2-muted">{t('reservation.noPlayers')}</span>
                                            )}
                                        </td>
                                        <td>{renderZone(row)}</td>
                                        <td className="is-num">
                                            <button
                                                type="button"
                                                className="c2-icon-btn c2-icon-btn--sm c2-icon-btn--danger"
                                                onClick={() => removeRow(row.id)}
                                                title={t('common.delete')}
                                                aria-label={t('common.delete')}
                                                disabled={rows.length === 1}
                                            >
                                                <Icon name="trash" size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="c2-row resa-table__footer">
                    <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={addRow}>
                        <Icon name="plus" size={16} />
                        {t('reservation.addRow')}
                    </button>
                    <div className="resa-bulk">
                        <span>{t('resaEdit.bulkAdd', 'Ajouter')}</span>
                        <input
                            type="number"
                            min="1"
                            max="200"
                            className="c2-input c2-input--sm resa-bulk__count"
                            value={bulkCount}
                            onChange={e => setBulkCount(e.target.value)}
                            aria-label={t('resaEdit.bulkCount', 'Nombre de lignes à ajouter')}
                        />
                        <span>{t('resaEdit.bulkRowsWith', 'lignes avec')}</span>
                        <select
                            className="c2-select c2-input--sm resa-bulk__season"
                            value={bulkSaisonId}
                            onChange={e => setBulkSaisonId(e.target.value)}
                            aria-label={t('resaEdit.bulkSeason', 'Saison des nouvelles lignes')}
                        >
                            <option value="">{t('resaEdit.bulkNoSeason', '— sans saison —')}</option>
                            {seasonOptions}
                        </select>
                        <button type="button" className="c2-btn c2-btn--primary c2-btn--sm" onClick={addBulkRows} disabled={!(parseInt(bulkCount, 10) > 0)}>
                            <Icon name="plus" size={16} />
                            {t('resaEdit.bulkAddBtn', 'Ajouter les lignes')}
                        </button>
                    </div>
                </div>
            </section>

            {/* ── Publication Discord ── */}
            <section className="c2-card c2-section">
                <h2 className="c2-section__title"><Icon name="hash" size={20} />{t('resaEdit.publishTitle', 'Publier dans un salon')}</h2>
                <p className="c2-section__desc">{t('resaEdit.publishDesc', 'Envoie la fiche telle qu\'elle est enregistrée dans un salon Discord.')}</p>
                <div className="c2-row">
                    <label className="c2-field resa-publish__field">
                        <span className="c2-field__label">{t('resaEdit.destination', 'Salon de destination')}</span>
                        <select className="c2-select" value={publishChannelId} onChange={e => setPublishChannelId(e.target.value)}>
                            <option value="">{t('resaEdit.selectChannel', '— Sélectionner un salon —')}</option>
                            {channels.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
                        </select>
                    </label>
                    <button
                        type="button"
                        className="c2-btn c2-btn--accent"
                        onClick={publishSession}
                        disabled={publishing || !activeSessionId || !publishChannelId}
                    >
                        <Icon name="arrowRight" size={18} />
                        {publishing ? t('editSeason.publishing', 'Publication...') : t('editSeason.publish', 'Publier')}
                    </button>
                </div>
            </section>

            {/* ── Zone de danger ── */}
            <section className="c2-card c2-section">
                <h2 className="c2-section__title"><Icon name="trash" size={20} />{t('resaList.deleteSheet', 'Supprimer la fiche')}</h2>
                <p className="c2-section__desc">{t('resaEdit.deleteDesc', 'Supprime définitivement la fiche et toutes ses réservations.')}</p>
                <div className="c2-row">
                    <button type="button" className="c2-btn c2-btn--danger" onClick={deleteSession} disabled={!activeSessionId}>
                        <Icon name="trash" size={18} />
                        {t('resaList.deleteSheet', 'Supprimer la fiche')}
                    </button>
                </div>
            </section>

            {/* ── Modale préférences de zone d'un joueur ── */}
            {editingPrefsModal && (
                <div className="c2-modal-backdrop" role="presentation" onClick={() => setEditingPrefsModal(null)}>
                    <div className="c2-card c2-modal resa-prefs" role="dialog" aria-modal="true" aria-labelledby="resa-prefs-title" onClick={e => e.stopPropagation()}>
                        <div className="c2-modal__head">
                            <h2 id="resa-prefs-title">{t('resaEdit.prefsOf', 'Préférences de {name}', { name: getPlayerName(editingPrefsModal.joueurId) })}</h2>
                            <button type="button" className="c2-icon-btn" onClick={() => setEditingPrefsModal(null)} aria-label={t('common.close', 'Fermer')}>
                                <Icon name="close" size={20} />
                            </button>
                        </div>
                        <p className="c2-muted">{t('resaEdit.prefsDesc', 'Zones par ordre de priorité, enregistrées pour ce joueur.')}</p>
                        <ol className="resa-prefs__list">
                            {editingPrefsModal.prefs.map((pref, i) => (
                                <li key={i} className="resa-prefs__row">
                                    <span className="resa-prefs__num">{i + 1}</span>
                                    <select
                                        className="c2-select c2-input--sm"
                                        value={pref.zone}
                                        onChange={e => {
                                            const zone = e.target.value;
                                            const zData = serverZones.find(z => z.zone === zone);
                                            setEditingPrefsModal(prev => ({
                                                ...prev,
                                                prefs: prev.prefs.map((p, idx) => (idx === i ? { zone, donjon: zData?.donjon || '' } : p))
                                            }));
                                        }}
                                        aria-label={t('resaEdit.prefN', 'Préférence {n}', { n: i + 1 })}
                                    >
                                        <option value="">{t('resaEdit.pickZone', '— Choisir une zone —')}</option>
                                        {serverZones.map(z => <option key={z.zone} value={z.zone}>{z.zone}</option>)}
                                    </select>
                                    <button
                                        type="button"
                                        className="c2-icon-btn c2-icon-btn--sm c2-icon-btn--danger"
                                        onClick={() => setEditingPrefsModal(prev => ({ ...prev, prefs: prev.prefs.filter((_, idx) => idx !== i) }))}
                                        aria-label={t('resaEdit.removePrefN', 'Retirer la préférence {n}', { n: i + 1 })}
                                    >
                                        <Icon name="trash" size={16} />
                                    </button>
                                </li>
                            ))}
                        </ol>
                        <button
                            type="button"
                            className="c2-btn c2-btn--ghost c2-btn--sm"
                            onClick={() => setEditingPrefsModal(prev => ({ ...prev, prefs: [...prev.prefs, { zone: '', donjon: '' }] }))}
                        >
                            <Icon name="plus" size={16} />
                            {t('zonesAdmin.addTitle', 'Ajouter une zone')}
                        </button>
                        <div className="c2-modal__actions">
                            <button type="button" className="c2-btn c2-btn--ghost" onClick={() => setEditingPrefsModal(null)}>{t('common.cancel', 'Annuler')}</button>
                            <button type="button" className="c2-btn c2-btn--primary" onClick={savePrefsForPlayer}>
                                <Icon name="save" size={18} />
                                {t('editSeason.save', 'Enregistrer')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReservationPage;
